import React, { useEffect, useRef, useState } from "react";
import { X, Camera, AlertTriangle } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

/**
 * Camera-based barcode scanner modal. Calls onDetected(text) once a barcode is
 * successfully decoded. Works with phone rear cameras on https://. Supports
 * UPC-A / EAN-13 / Code-128 / QR.
 */
export default function BarcodeScanner({ open, onClose, onDetected }) {
  const [err, setErr] = useState("");
  const [starting, setStarting] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "barcode-scanner-region";

  useEffect(() => {
    if (!open) return;
    setErr("");
    setStarting(true);
    const config = { fps: 10, qrbox: { width: 280, height: 160 }, aspectRatio: 1.7 };
    let cancelled = false;

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = scanner;
        // Prefer the rear-facing camera on phones
        const cams = await Html5Qrcode.getCameras();
        if (cancelled) return;
        const rear = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[0];
        if (!rear) {
          setErr("No camera detected.");
          setStarting(false);
          return;
        }
        await scanner.start(
          rear.id,
          config,
          (decoded) => {
            // Throttle: stop scanning after first successful read
            scanner.stop().then(() => scanner.clear()).catch(() => {});
            onDetected?.(decoded);
          },
          () => {}, // ignore per-frame errors
        );
        setStarting(false);
      } catch (e) {
        setErr(e?.message || "Failed to start camera. Make sure you granted camera permission.");
        setStarting(false);
      }
    };
    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [open, onDetected]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.9)" }} data-testid="barcode-scanner-modal">
      <div className="surface p-5 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 label-eyebrow" style={{ color: "var(--ab-primary)" }}>
            <Camera size={14} /> Barcode Scanner
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="barcode-scanner-close"><X size={18} /></button>
        </div>
        {err ? (
          <div className="p-4 text-sm flex items-start gap-2" style={{ color: "var(--ab-danger)", border: "1px solid var(--ab-danger)" }} data-testid="barcode-scanner-error">
            <AlertTriangle size={16} /> {err}
          </div>
        ) : (
          <>
            <div id={containerId} style={{ width: "100%", minHeight: 280, background: "#000" }} />
            <div className="text-xs mt-3" style={{ color: "var(--ab-text-dim)" }}>
              {starting ? "Starting camera…" : "Centre the barcode inside the box. Lookup runs as soon as it's read."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
